import posthog from 'posthog-js';

export type ProductAnalyticsProperties = Record<string, boolean | number | string | null | undefined>;

let initialized = false;

function definedProperties(properties: ProductAnalyticsProperties): Record<string, boolean | number | string> {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== null && value !== undefined),
  ) as Record<string, boolean | number | string>;
}

/**
 * Initializes broad, automatic interaction capture while custom events below
 * remain the stable source for CRM workflow reporting.
 */
export function initializeProductAnalytics(): boolean {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  if (!apiKey || initialized) return initialized;

  posthog.init(apiKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: true,
    capture_exceptions: true,
    person_profiles: 'identified_only',
  });
  initialized = true;
  return true;
}

/** Captures a versioned, application-owned event for durable product analysis. */
export function captureProductEvent(event: string, properties: ProductAnalyticsProperties = {}): void {
  if (!initialized) return;
  posthog.capture(event.replace(/\./g, '_'), definedProperties({ eventVersion: 1, ...properties }));
}

export function identifyProductUser(user: {
  authUserId: string;
  appUserId?: number;
  name?: string | null;
  email?: string | null;
  isSuperAdmin?: boolean;
}): void {
  if (!initialized) return;
  posthog.identify(user.authUserId, definedProperties({
    appUserId: user.appUserId,
    name: user.name,
    email: user.email,
    isSuperAdmin: user.isSuperAdmin,
  }));
}

export function resetProductUser(): void {
  if (initialized) posthog.reset();
}
